import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

// stuff for dialog - move if separating
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';

import AddIcon from '@material-ui/icons/Add';

import PaginatedTable from 'app/components/tables/PaginatedTable';
import EditProposalDialog from 'app/components/election/EditProposalDialog';

import useApp from 'app/context/useApp';

const useFilterStyles = makeStyles(theme => ({
    subSection: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[200]
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.grey[200]
    },
    dialogSpacing: {
        padding: theme.spacing(1)
    },
    noSpacing: {
        padding: 0
    },
    noMarginLeft: {
        marginLeft: 0
    },
    empty: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    noContentPadding: {
        padding: 0
    }
}));

const statuses = ['accepted', 'active', 'rejected'];
const tableFieldTypes = ['textbox', 'textarea', 'number', 'date', 'time', 'datetime', 'options', 'email', 'userselect'];

const FilterProposalsDialog = (props) => {

    const classes = useFilterStyles()

    const { open, onClose, onFilter } = props;

    const [ filters, setFilters ] = useState([]);
    const [ statusChecked, setStatusChecked ] = useState([]);

    const { app } = useApp();

    // Get Functions ------------------------------------------

    const getFilters = () => {
        const newFilters = app.user.application_state.proposals.filters;
        const activeFilters = [];

        if (newFilters.status && newFilters.status instanceof Array && newFilters.status.length > 0) {
            setStatusChecked(newFilters.status);
            activeFilters.push('status')
        }

        setFilters(activeFilters);
    }

    useEffect(() => {
        getFilters();
    }, []);

    // Handlers ------------------------------------

    const handleFilter = filter => {
        const newFilters = [...filters];
        const i = newFilters.indexOf(filter)

        if (i > -1) {
            newFilters.splice(i, 1)
        } else {
            newFilters.push(filter)
        }

        setFilters(newFilters);
    }

    const handleStatus = id => {
        const newChecked = [...statusChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setStatusChecked(newChecked);
    }

    const handleClose = () => {
        onClose();
    }

    // Posters -----------------------------

    const postFilter = () => {

        const data = {}

        if (filters.indexOf('status') > -1 && statusChecked.length > 0) {
            data.status = statusChecked;
        }

        // console.log(data)

        onFilter(data);
        onClose();
    }

    const postClear = () => {
        setStatusChecked([]);
        setFilters([]);
        onFilter({});
        onClose();
    }

    return (
        <Dialog
            maxWidth="xs"
            open={open}
            onClose={handleClose}
            fullWidth
            aria-labelledby="filter-proposals"
            >
            <DialogTitle className={classes.dialogSpacing}>
                <Grid container alignItems="center" justify="space-between">
                    <Grid item>
                        <Button color="primary" onClick={postClear} variant="outlined">
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        Filters
                    </Grid>
                    <Grid item>
                        <Button color="primary" variant="contained" onClick={postFilter}>
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <Divider />
            <DialogContent className={classes.noContentPadding}>
                <FormControlLabel
                    className={classes.noMarginLeft}
                    control={
                        <Checkbox
                            checked={filters.indexOf('status') > -1}
                            onChange={() => handleFilter('status')}
                            name="status"
                            color="primary"
                        />
                    }
                    label="Status"
                />
                <Divider />
                <Collapse in={filters.indexOf('status') > -1}>
                    <FormGroup className={classes.subSection}>
                        {statuses.map(status => (
                            <FormControlLabel
                                className={classes.noMarginLeft}
                                key={status.id}
                                control={
                                    <Checkbox
                                        checked={(statusChecked.indexOf(status.id) > -1)}
                                        onChange={() => handleStatus(status.id)}
                                        name={status}
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label={window.ucFirst(status)}
                            />
                        ))}
                    </FormGroup>
                </Collapse>
            </DialogContent>
        </Dialog>
    )
}

const newProposal = {
    id: 0,
    values: [],
    user_id: 0
}

const ProposalsToolbar = (props) => {

    const { election, getElection } = props;

    const [ newProposalDialogOpen, setNewProposalDialogOpen ] = useState(false);

    const openNewProposalDialog = () => {
        setNewProposalDialogOpen(true);
    }

    const closeNewProposalDialog = () => {
        setNewProposalDialogOpen(false);
    }

    const closeNewProposalDialogAndRefresh = () => {
        closeNewProposalDialog();
        getElection();
    }

    return (
        <>
            <Tooltip title="Add New Proposal">
                <IconButton
                    aria-label="Add New Proposal"
                    onClick={openNewProposalDialog}
                    >
                    <AddIcon />
                </IconButton>
            </Tooltip>
            <EditProposalDialog
                election={election}
                proposal={{...newProposal}}
                open={newProposalDialogOpen}
                onClose={closeNewProposalDialog}
                onSave={closeNewProposalDialogAndRefresh}
                onDelete={closeNewProposalDialogAndRefresh}
                admin
            />
        </>
    );
}

export default function Proposals(props) {

    const {
        election,
        electionID
    } = props;

    const [ columns, setColumns ] = useState([]);
    const [ viewableColumns, setViewableColumns ] = useState([]);
    const [ orderBy, setOrderBy ] = useState(null);
    const [ order, setOrder ] = useState(null);

    useEffect(() => {

        const cols = [
            { id: 'title', align: 'left', disablePadding: false, label: 'Title' },
            { id: 'subtitle', align: 'left', disablePadding: false, label: 'Subtitle' },
        ]
        const viewableCols = [];
        let oBy = null;
        let oDir = null;

        election.fields.forEach(f => {

            if (tableFieldTypes.indexOf(f.options.type) > -1) {

                if (f.options?.table) {
                    viewableCols.push(f.id.toString())

                    if (f.options?.order_by) {
                        oBy = f.id.toString()
                        oDir = f.options.order
                    }
                }

                cols.push({
                    id: f.id.toString(),
                    label: f.options.label,
                    align: (f.options.type === 'number' || f.options.type === 'currency') ? 'right' : 'left',
                    disablePadding: false,
                    type: f.options.type,
                    is_value: true,
                    format: f.options.format
                })
            }
        })

        cols.push(...[
            { id: 'recommendations_count', align: 'right', disablePadding: false, label: 'Recommendations'},
            { id: 'recommendation_users', align: 'left', disablePadding: false, label: 'Recommendation Users', type: 'textarea'},
            { id: 'votes_count', align: 'right', disablePadding: false, label: 'Votes'},
            { id: 'sponsors', align: 'left', disablePadding: false, label: 'Sponsors', type: 'textarea'},
            { id: 'sponsors_emails', align: 'left', disablePadding: false, label: 'Sponsor Emails', type: 'textarea'},
            { id: 'cycles_repeated', align: 'right', disablePadding: false, label: 'Cycles Proposed'},
            {
                id: 'created_at',
                align: 'left',
                disablePadding: false,
                label: 'Date Created',
                type: 'date',
                format: 'D'
            },
            {
                id: 'status',
                align: 'left',
                disablePadding: true,
                label: 'Status',
                type: 'chip',
                map: {
                    'active': 'chipPrimary',
                    'accepted': 'chipSuccess',
                    'rejected': 'chipError',
                }
            }
        ]);

        setColumns(cols);
        setViewableColumns(viewableCols);
        setOrderBy(oBy);
        setOrder(oDir);

    }, [election]);

    return (columns.length > 1 ) ? (
        <PaginatedTable
            columns={columns}
            stateKey="proposals"
            label="Proposals"
            tableUrl={`/election/${electionID}`}
            baseUrl='/admin/election/proposal'
            NewDialog={<EditProposalDialog election={election} id={0} admin/>}
            EditDialog={<EditProposalDialog election={election} admin/>}
            initViewableColumns={viewableColumns}
            initOrderBy={orderBy}
            initOrder={order}
            exportTitle={`${election.name} Export`}
            exportAllTitle={`${election.name} Export`}
        />
    ) : null;
}
